import { useCallback, useEffect, useMemo, useState } from "react";
import {
    CreatorProduct,
    CreatorProductEntitlement,
    CreatorProductEntitlementsBindingModelDiscriminator,
    CreatorProductEntitlementsRequest
} from "@switcherstudio/switcher-api-client";
import { Button } from "components/buttons/Button";
import { PricingSelectModal } from "components/modal/PricingSelectModal";
import { useCreatorProductEntitlement } from "hooks/useCreatorProductEntitlement";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { exists } from "helpers/booleans";
import { AttentionModal } from "components/modal/AttentionModal";
import { useNavigate } from "react-router-dom";
import { AttentionModalTable } from "components/modal/AttentionModal/AttentionModalTable";
import { useRunOnce } from "hooks/useRunOnce";
import { useUserStripeData } from "hooks/useUserStripeData";
import { StripeConnectWrapper } from "components/stripe/StripeConnectWrapper";
import { useDispatch } from "react-redux";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";

export interface CollectionGatedContentPageStripeSectionProps {
    videoPlayerId: string;
    onReset?: () => void;
    setHandleSubmit?: React.Dispatch<
        React.SetStateAction<(() => Promise<boolean>) | null>
    >;
    setHasChanges?: (hasChanges: boolean) => void;
    setSelectedValue?: (selectedValue: string) => void;
    setIsLoading?: (isLoading: boolean) => void;
    disabled?: boolean;
}

export const CollectionGatedContentPageStripeSection = ({
    videoPlayerId,
    onReset,
    setHandleSubmit,
    setHasChanges,
    setSelectedValue,
    setIsLoading,
    disabled = false
}: CollectionGatedContentPageStripeSectionProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [localEntitlement, setLocalEntitlement] =
        useState<CreatorProductEntitlementsRequest>();

    const [pricingSelectModalOpen, setPricingSelectModalOpen] =
        useState<boolean>(false);
    const [attentionModalOpen, setAttentionModalOpen] =
        useState<boolean>(false);
    const [existingEntitlement, setExistingEntitlement] =
        useState<CreatorProductEntitlement>();

    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        setIsLoading?.(loading);
    }, [loading, setIsLoading]);

    // set the initial loading state to true
    useEffect(() => {
        setIsLoading?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        accountData: { gatedContentStatus },
        productData: { products, recurringProducts, oneTimeProducts },
        loading: stripeLoading
    } = useUserStripeData({
        includeProducts: true,
        requestImmediately: true
    });

    const resetData = useCallback(() => {
        setExistingEntitlement(null);
        setAttentionModalOpen(false);
        setPricingSelectModalOpen(false);
        onReset?.();
    }, [onReset]);

    const {
        loading: creatorProductEntitlementLoading,
        videoPlayer,
        getVideoPlayer,
        addCreatorProductEntitlement,
        deleteCreatorProductEntitlement
    } = useCreatorProductEntitlement(videoPlayerId, {
        suppressNotifications: true,
        onAdd: resetData,
        onDelete: resetData
    });

    useRunOnce(
        () => setLoading(false),
        !creatorProductEntitlementLoading && !stripeLoading
    );

    const videoPlayerEntitlement = useMemo<CreatorProductEntitlement>(
        () =>
            videoPlayer?.VideoPlayerEntitlements.ProductEntitlements.find(
                (e) => e.Product.IsActive && e.Product.Prices
            ),
        [videoPlayer]
    );

    const openPricingModal = useCallback(() => {
        setPricingSelectModalOpen(true);
    }, []);

    const productHasEntitlement = exists(videoPlayerEntitlement);

    useEffect(() => {
        setLocalEntitlement(
            productHasEntitlement
                ? {
                      ProductEntitlements: [
                          {
                              ProductId:
                                  videoPlayer?.VideoPlayerEntitlements
                                      ?.ProductEntitlements?.[0]?.ProductId,
                              VideoPlayerId: videoPlayer?.ProjectId,
                              Discriminator:
                                  CreatorProductEntitlementsBindingModelDiscriminator._0
                          }
                      ]
                  }
                : undefined
        );
        setExistingEntitlement(videoPlayerEntitlement);
    }, [videoPlayer, productHasEntitlement, videoPlayerEntitlement]);

    const handleAssignment = useCallback(
        async (creatorProduct: CreatorProduct) => {
            if (creatorProduct !== undefined) {
                setLocalEntitlement({
                    ProductEntitlements: [
                        {
                            ProductId: creatorProduct?.Id,
                            VideoPlayerId: videoPlayerId,
                            Discriminator:
                                CreatorProductEntitlementsBindingModelDiscriminator._0
                        }
                    ]
                });
            }
        },
        [videoPlayerId]
    );

    const performSubmission = useCallback(async () => {
        if (!!localEntitlement) {
            await addCreatorProductEntitlement([localEntitlement]);
        } else if (!!existingEntitlement) {
            await deleteCreatorProductEntitlement([existingEntitlement?.Id]);
        }

        resetData();
        await getVideoPlayer();
    }, [
        localEntitlement,
        existingEntitlement,
        addCreatorProductEntitlement,
        deleteCreatorProductEntitlement,
        resetData,
        getVideoPlayer
    ]);

    const hasChanges = useMemo<boolean>(() => {
        const localProductId =
            localEntitlement?.ProductEntitlements?.[0]?.ProductId;
        const existingProductId = existingEntitlement?.Product?.Id;

        return localProductId !== existingProductId;
    }, [localEntitlement, existingEntitlement]);

    useEffect(() => {
        if (setHasChanges) {
            setHasChanges(hasChanges);
        }
    }, [hasChanges, setHasChanges]);

    const handleSubmit = useCallback(async () => {
        if (!hasChanges) {
            return true;
        }

        const currentProduct = products.find(
            (p) => p.Id === existingEntitlement?.ProductId
        );

        // Show attention modal if the existing product has active subscriptions and is being removed or replaced.
        if (
            !!existingEntitlement &&
            (currentProduct?.ActiveSubscriptions > 0 ||
                currentProduct?.Purchases > 0)
        ) {
            setAttentionModalOpen(true);
            return false;
        }

        await performSubmission();
        return true;
    }, [products, existingEntitlement, hasChanges, performSubmission]);

    // Pass the handleSubmit function to the parent component
    useEffect(() => {
        if (setHandleSubmit) {
            setHandleSubmit(() => async () => {
                return await handleSubmit();
            });
        }
    }, [setHandleSubmit, handleSubmit]);

    const filteredProducts = useMemo<CreatorProduct[]>(() => {
        if (!oneTimeProducts && !recurringProducts) return [];
        const recurringFiltered =
            recurringProducts?.filter((p) => p.Name !== "Catalog Pass") ?? [];
        return [...(oneTimeProducts ?? []), ...recurringFiltered];
    }, [recurringProducts, oneTimeProducts]);

    /** this needed to be set in state because we don't PUT
     * the new pass til we click the save button, but we need the name
     * to be correct once the user has selected a new pass in the modal */
    const passName = useMemo<string>(() => {
        if (
            localEntitlement?.ProductEntitlements?.[0]?.ProductId !== undefined
        ) {
            return filteredProducts?.find(
                (fp) =>
                    fp.Id ===
                    localEntitlement?.ProductEntitlements?.[0]?.ProductId
            )?.Name;
        } else if (localEntitlement === undefined) {
            return t("video-player-settings:add-pricing");
        }
        return t("video-player-settings:add-pricing");
    }, [localEntitlement, filteredProducts, t]);

    const selectPass = useCallback(
        async (selection) => {
            if (selection === undefined) {
                setLocalEntitlement(undefined);
                return;
            }

            if (
                existingEntitlement &&
                selection?.Id === existingEntitlement?.Product?.Id
            ) {
                setLocalEntitlement({
                    ProductEntitlements: [
                        {
                            ProductId: existingEntitlement.Product.Id,
                            VideoPlayerId: videoPlayerId,
                            Discriminator:
                                CreatorProductEntitlementsBindingModelDiscriminator._0
                        }
                    ]
                });

                return;
            }
            handleAssignment(selection);
        },
        [existingEntitlement, handleAssignment, videoPlayerId]
    );

    useEffect(() => {
        setSelectedValue(localEntitlement?.ProductEntitlements?.[0]?.ProductId);
    }, [localEntitlement, setSelectedValue]);

    const handleCancelAttentionModal = useCallback(() => {
        if (existingEntitlement) {
            setLocalEntitlement({
                ProductEntitlements: [
                    {
                        ProductId: existingEntitlement.Product.Id
                    }
                ]
            });
        } else {
            setLocalEntitlement(undefined);
        }
        setAttentionModalOpen(false);
        setPricingSelectModalOpen(false);

        return false;
    }, [existingEntitlement]);

    const handleContinueAttentionModal = useCallback(async () => {
        setPricingSelectModalOpen(false);
        setAttentionModalOpen(false);

        try {
            await performSubmission();

            dispatch(
                addNotification({
                    type: NotificationType.Success,
                    message: t("players:messages:gated-options-update-success")
                })
            );
        } catch (error) {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message:
                        error.message || t("players:messages:update-failed")
                })
            );
        }

        return true;
    }, [dispatch, performSubmission, t]);

    return (
        <div className={styles["page-content"]}>
            <StripeConnectWrapper
                showLinkToManageGatedContentPasses={
                    filteredProducts?.length === 0
                }
                additionalLoading={loading}
                inlineLoading={true}
            >
                <div
                    className={`${styles["pass-container"]} ${styles["recurring-pass"]}`}
                >
                    <Button
                        disabled={
                            gatedContentStatus !== GatedContentStatus.READY ||
                            disabled
                        }
                        type="badge"
                        isActive={!!localEntitlement}
                        onClick={() => {
                            openPricingModal();
                        }}
                    >
                        {passName}
                    </Button>
                </div>
            </StripeConnectWrapper>

            {/* Unconditionally Rendered Modals */}

            <PricingSelectModal
                isOpen={pricingSelectModalOpen}
                setIsOpen={setPricingSelectModalOpen}
                handleSelect={(selected) => selectPass(selected)}
                products={products}
                selected={localEntitlement?.ProductEntitlements?.[0]?.ProductId}
                buttonText={t("buttons:done")}
            />

            <AttentionModal
                isOpen={attentionModalOpen}
                setIsOpen={setAttentionModalOpen}
                handleContinue={handleContinueAttentionModal}
                handleCancel={handleCancelAttentionModal}
                updateModal={() =>
                    navigate("/gated-content", {
                        state: {
                            searchParams: {
                                pass: videoPlayerEntitlement.ProductId
                            }
                        }
                    })
                }
                continueText={t("buttons:continue")}
            >
                <>
                    <div>
                        <p
                            className={`${styles["undo-mb"]} ${styles["padding-bottom"]}`}
                        >
                            {t("gated-content-page:attention-body")}
                        </p>
                    </div>
                    <AttentionModalTable
                        players={[videoPlayer]}
                        updateModal={() =>
                            navigate("/gated-content", {
                                state: {
                                    searchParams: {
                                        pass: videoPlayerEntitlement.ProductId
                                    }
                                }
                            })
                        }
                    />
                </>
            </AttentionModal>
        </div>
    );
};
